import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import dayjs from 'dayjs';
import qs from 'qs';
import { deleteCookie } from 'utils/bom-dom-manipulation/cookies';
import LS from 'utils/bom-dom-manipulation/local-storage';
import { MILLISECONDS_IN_SECOND } from 'utils/constants/dateInfo/time-numbers';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { showToastError } from 'utils/generic/show-toast-error';
import { storageKeyName } from 'utils/generic/storage-key-name';
import { ECasinoGameMode } from 'interfaces/casino-data';
import { logout } from 'services/authentication';
import { getSavedLocation, saveCurrentLocation } from 'components/QueryRoute';
import {
  removeUserData,
  setClientGamingStateInfo,
  setUserOptIns
} from 'store/actions/user-data';
import { getSectionsData } from 'store/selectors/app-data';
import {
  getIsLoggedIn,
  getLoginLimit,
  getUser
} from 'store/selectors/user-data';
import { useOpenLoginPopup } from '../account/useOpenLoginPopup';
import { useSearchParams } from '../useSearchParams';

export const useLoginLimit = (): void => {
  const [limitTimeout, setLimitTimeout] = useState<null | ReturnType<
    typeof setTimeout
  >>(null);

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const isLoggedIn = useSelector(getIsLoggedIn);
  const loginLimit = useSelector(getLoginLimit);
  const { last_login_date: lastLogin } = useSelector(getUser);
  const accountSections = useSelector(getSectionsData);
  const searchParams = useSearchParams();
  const openLoginPopup = useOpenLoginPopup();

  const removeUser = () => {
    dispatch(removeUserData());
    dispatch(setUserOptIns(null));
    dispatch(setClientGamingStateInfo(null));

    deleteCookie('loginCredentials');

    const { accounts } = qs.parse(window.location.search, {
      ignoreQueryPrefix: true
    });

    accounts && history.push(getSavedLocation());
  };

  useEffect(() => {
    const loginLimitTabEnabled =
      accountSections &&
      !accountSections?.myProfile?.find(tab => tab?.formType === 'loginLimit')
        ?.hidden;

    if (loginLimitTabEnabled && isLoggedIn && loginLimit && lastLogin) {
      const limitStartTime = LS.getItem(
        storageKeyName('account', 'LOGIN_LIMIT_START_TIME')
      );

      const delay = lastLogin + loginLimit - dayjs().unix();

      const timer = delay > 0 ? delay : 1;

      if (!SpringConfigs.LOGIN_LIMIT_POPUP) {
        setLimitTimeout(
          setTimeout(
            () => {
              logout(() => {
                removeUser();
                showToastError(t('account.sessionExpired'));

                if (searchParams.mode === ECasinoGameMode.REAL) {
                  saveCurrentLocation();
                  openLoginPopup();
                }
              });
            },
            limitStartTime !== 'null'
              ? +limitStartTime
              : timer * MILLISECONDS_IN_SECOND
          )
        );
      }
    }

    return () => {
      if (limitTimeout) {
        clearTimeout(limitTimeout);
        setLimitTimeout(null);
      }
    };
  }, [isLoggedIn, loginLimit, lastLogin, accountSections]);
};
